"use client";

import type { ReactNode } from "react";
import { motion } from "framer-motion";
import styles from "./contact-link.module.css";
import Button from "@/ui/shared/components/button/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/ui/shared/components/card/card";

interface ContactLinkProps {
  icon: ReactNode;
  title: string;
  email?: string;
  description?: string;
  href: string;
  buttonText: string;
  /** When true, skip entrance animation (e.g. locale switch). */
  skipAnimation?: boolean;
}

export function ContactLink({
  icon,
  title,
  email,
  description,
  href,
  buttonText,
  skipAnimation = false,
}: ContactLinkProps) {
  return (
    <motion.div
      initial={skipAnimation ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      whileInView={skipAnimation ? undefined : { opacity: 1, y: 0 }}
      animate={skipAnimation ? { opacity: 1, y: 0 } : undefined}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.2 }}
    >
      <Card className={styles.card}>
        <CardHeader>
          <CardTitle className={styles.title}>{title}</CardTitle>
          {email ? (
            <p className={styles.emailRow}>
              <a className={styles.emailLink} href={`mailto:${email}`}>
                <span className={styles.emailIcon} aria-hidden="true">
                  {icon}
                </span>
                <span>{email}</span>
              </a>
            </p>
          ) : null}
          {description ? (
            <CardDescription className={styles.description}>{description}</CardDescription>
          ) : null}
        </CardHeader>
        <CardFooter>
          <Button
            href={href}
            variant="accent-brand"
            iconPosition="right"
            size="md"
            icon={
              <svg
                className="rtlFlipH"
                width="20"
                height="20"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.14645 3.14645C8.34171 2.95118 8.65829 2.95118 8.85355 3.14645L12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L8.85355 11.8536C8.65829 12.0488 8.34171 12.0488 8.14645 11.8536C7.95118 11.6583 7.95118 11.3417 8.14645 11.1464L11.2929 8H2.5C2.22386 8 2 7.77614 2 7.5C2 7.22386 2.22386 7 2.5 7H11.2929L8.14645 3.85355C7.95118 3.65829 7.95118 3.34171 8.14645 3.14645Z"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                ></path>
              </svg>
            }
          >
            {buttonText}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
